import { Model, Factory, belongsTo, hasMany } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randNumber, randSoonDate } from '@ngneat/falso'

import type { SchemaAttributes } from '../types.js'
import type { MockServer } from './server.js'
import { createErrorResponse } from './utils.js'
import { addToDate } from '@/utils/date/calculation.js'

export type CalendarAppointmentDetailData =
  SchemaAttributes<'CalendarAppointment'>

export const model = (
  Model as ModelDefinition<CalendarAppointmentDetailData>
).extend({
  client: belongsTo('client'),
  patients: hasMany('patient'),
  complaintType: belongsTo('complaintType'),
  user: belongsTo('user'),
})

export const factory = Factory.extend<CalendarAppointmentDetailData>({
  start() {
    return randSoonDate({ days: 30 }).toISOString()
  },
  end() {
    const duration = randNumber({ min: 15, max: 90 })
    const endDate = addToDate(this.start as string, { minutes: duration })
    return endDate.toISOString()
  },
  complaint() {
    return 'Routine checkup'
  },
  appointment_type() {
    return null
  },
  afterCreate(appointment, server) {
    const hasPatients = appointment.patients?.models?.length > 0
    const patient = hasPatients
      ? appointment.patients.models[0]
      : server.create('patient', 'withBreed')

    appointment.update({
      ...(!hasPatients && { patients: [patient] }),
      ...(!appointment.user && { user: server.create('user') }),
      ...(!appointment.client && { client: patient.client }),
      ...(!appointment.complaintType && {
        complaintType: server.create('complaintType'),
      }),
    })
  },
})

export function routes(server: MockServer) {
  server.resource('calendarAppointment', { path: '/appointments' })

  server.post('/appointments', function (schema, request) {
    const requestBody = JSON.parse(request.requestBody)
    const { attributes, relationships } = requestBody.data

    if (!attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }

    if (!relationships) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "relationships" in request body' }],
      })
    }

    // Validate required relationships
    if (!relationships.client?.data?.id) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "client" relationship' }],
      })
    }

    if (!relationships.patients?.data?.length) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'At least one patient is required' }],
      })
    }

    if (!relationships.complaint_type?.data?.id) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "complaint_type" relationship' }],
      })
    }

    if (!relationships.user?.data?.id) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "user" relationship' }],
      })
    }

    const appointment = schema.create('calendarAppointment', {
      ...attributes,
      clientId: relationships.client.data.id,
      complaintTypeId: relationships.complaint_type.data.id,
      userId: relationships.user.data.id,
    })

    // add patients to the appointment
    const patientIds = relationships.patients.data.map(
      (p: { id: string }) => p.id,
    )
    const patients = schema.where('patient', (patient) =>
      patientIds.includes(patient.id),
    )

    appointment.update({ patients: patients.models })

    return appointment
  })
}
